import { Trans } from '@lingui/macro';
import { useReducer, useState, useEffect } from "react";
import { AIBot } from "../../../models/AIBot";
import { Room } from "../../../models/Room";
import { Fire } from "../../../services/FireClient";
import LimitedFeature from "../../components/LimitedFeature";
import { Avatar, AvatarFallback, AvatarImage } from "../../../components/ui/avatar";
import { Button } from "../../../components/ui/button";
import { Checkbox } from "../../../components/ui/checkbox";
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from "../../../components/ui/card";
import { useToast } from "../../../hooks/use-toast";

const GENERAL_BOTS = [new AIBot('ai-dalle'), new AIBot('ai-search'), new AIBot('ai-chatgpt')];

export const BotUtilityPart: React.FC<{ room: Room }> = ({ room }) => {
  const [availableBots] = useState(GENERAL_BOTS)
  const { toast } = useToast();
  const [_, forceUpdate] = useReducer((x) => x + 1, 0);
  const [hasChanged, setHasChanged] = useState(false);

  // Reset hasChanged when component mounts or room changes
  useEffect(() => {
    setHasChanged(false);
  }, [room.id]);

  return (
    <Card className="w-full">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg font-medium"><Trans>Utility bots</Trans></CardTitle>
      </CardHeader>
      <CardContent className="grid gap-4">
        {availableBots.map(b => getBotItem(b))}
      </CardContent>
      {hasChanged && (
        <CardFooter>
          <Button onClick={save} className="w-full bg-green-600 hover:bg-green-700 text-white">
            <Trans>Save changes</Trans>
          </Button>
        </CardFooter>
      )}
    </Card>
  );

  function getBotItem(b: AIBot) {
    const selected = room.bots?.find(bot => bot.type === b.type);
    return (
      <div key={b.type} className="flex items-center space-x-4">
        <LimitedFeature
          workspaceId={room.workspaceId}
          tier={'STANDARD'}
          content={() => (
            <Checkbox
              checked={!!selected}
              onCheckedChange={(checked) => toggleBot(checked === true, b)}
              id={`bot-${b.type}`}
            />
          )}
        />
        <Avatar className="h-10 w-10">
          <AvatarImage src={`/assets/img/${b.type}.png`} alt={b.name} />
          <AvatarFallback>{b.name[0]}</AvatarFallback>
        </Avatar>
        <div className="flex-1 space-y-1">
          <p className="text-sm font-medium leading-none">
            <label htmlFor={`bot-${b.type}`} className="cursor-pointer">
              {selected?.name ?? b.name}
            </label>
          </p>
          <p className="text-xs text-muted-foreground truncate">
            {selected?.description ?? AIBot.getDescription(b.type)}
          </p>
        </div>
      </div>
    );
  }

  function save() {
    if (room.bots) {
      Fire.updateRoomBots(room.id, room.bots);
      setHasChanged(false);
      toast({ title: "Saved", description: "Changes saved", duration: 2000 });
    }
  }

  function toggleBot(checked: boolean, b: AIBot) {
    if (b.type === 'ai-chatgpt' && checked) {
      toast({
        title: "Deprecated",
        description: "This ChatGPT bot is deprecated and replaced with Soribots, which is more advanced and configurable.",
        duration: 3000
      });
      forceUpdate();
      return;
    }

    room.bots = room.bots ?? [];
    if (checked) {
      room.bots.push(b);
    } else {
      room.bots = room.bots.filter(bot => bot.type !== b.type)
    }
    setHasChanged(true);
    forceUpdate();
  }
}

export default BotUtilityPart;